// Apolo IDE — useTurnTrace (self-improve-hardening E4, renderer side).
//
// recordTurnTrace persists a finished turn's FULL, replayable trace to D1 at
// run-end (best-effort, fire-and-forget — the run already happened). fetchBadRuns
// / fetchTurnTrace power the bad-runs view. All behind the JWT'd worker endpoints.

import { apiFetch } from '../lib/api.js';

/**
 * Persist one TurnTrace. Best-effort: resolves `{ ok, error? }`, never throws, so
 * callers can `void recordTurnTrace(...)` at run-end. A persist FAILURE is returned
 * (and logged by the caller) — never silently swallowed.
 *
 * @param {Record<string, unknown>} record  built by lib/turnTraceRecord.js
 * @returns {Promise<{ ok: boolean, run_id?: string, turn_id?: string, error?: string }>}
 */
export async function recordTurnTrace(record, { token } = {}) {
  try {
    const res = await apiFetch('/api/code-ide/turn-trace', { method: 'POST', body: { record }, token });
    if (res.ok) return { ok: true, run_id: res.data?.run_id, turn_id: res.data?.turn_id };
    return { ok: false, error: res.error || `HTTP ${res.status}` };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
}

/** Fetch the user's bad runs (ABORT / red verdicts). Returns [] on error. */
export async function fetchBadRuns({ limit = 50, token } = {}) {
  try {
    const res = await apiFetch(`/api/code-ide/bad-runs?limit=${encodeURIComponent(limit)}`, { token });
    if (res.ok && Array.isArray(res.data?.runs)) return res.data.runs;
    return [];
  } catch {
    return [];
  }
}

/** Fetch one full persisted trace (the replay input). Returns null if not found. */
export async function fetchTurnTrace(runId, turnId, { token } = {}) {
  try {
    const res = await apiFetch(
      `/api/code-ide/turn-trace/${encodeURIComponent(runId)}/${encodeURIComponent(turnId)}`,
      { token },
    );
    if (res.ok && res.data?.trace) return res.data.trace;
    return null;
  } catch {
    return null;
  }
}
